import type {
  FormattingType,
  LayoutType,
  SectionItemDatasource,
  TemplateDataType,
  TemplatePresentationItemDisplayType,
  TemplatePresentationItemType,
  TemplateSectionItem,
  TemplateTextAlignType,
} from 'types';
import type { TemplateFormsState } from '../index';
import { type TranslationName } from 'types/translator';
import { translateValues } from 'components/mui/common/TranslationField';
import type { ConditionAttribute, FormatDateTime, FormatNumber, FormatText } from 'types/helios/templateForms';
import { LabelLayout } from 'types/helios/templateForms/formatting';
import { TemplateAggregationGroupType } from 'types/aggregationGroups';
import { generateGridPlaceholders } from 'utils/section/gridUtils';
import { findTemplateSectionIndex, isSectionNameUnique } from '../utils/nameUniqueness';

export interface Payload {
  value: {
    collapsed: boolean;
    sizeX: number;
    sizeY: number;
    name: string;
    title: TranslationName[];
    description: string;
  };
  layoutType: LayoutType;
  newTab?: boolean;
  newSubTab?: boolean;
  parentSectionName?: string;
  currentTab?: string | null;
  tabId?: string;
}

export const initialItem: TemplateSectionItem = {
  itemGuid: '',
  x: '0',
  y: '0',
  itemDatasource: {
    typeItemDatasource: 'ColumnValue' as TemplatePresentationItemType,
    templateDataItemId: null,
    agregationGroupId: null,
    userDefinedAttributeId: null,
    templateFunctionId: null,
  } as SectionItemDatasource,
  name: '',
  label: translateValues,
  buttonLabel: translateValues,
  dataType: 'String' as TemplateDataType,
  displayType: null as TemplatePresentationItemDisplayType | null,
  isTriggerEventsDisabled: true,
  ordinalIndex: null,
  isVisible: true,
  isEditable: true,
  isRequired: true,
  isPresaveAllowed: false,
  sortOrder: 0,
  sortType: 'None',
  isGrouped: false,
  groupingOrder: 0,
  aggregationType: TemplateAggregationGroupType.None,
  settings: {
    browse: null,
    staticSelection: null,
    externalFile: null,
    link: null,
    delimiter: null,
    infoText: null,
  },
  formattingType: 'None' as FormattingType,
  formatting: {
    formatValue: {
      textAlign: 'Left' as TemplateTextAlignType,
      typeSpecific: {
        formatText: null as FormatText | null,
        formatNumber: null as FormatNumber | null,
        formatDateTime: null as FormatDateTime | null,
      },
    },
    formatLabel: {
      textAlign: 'Left' as TemplateTextAlignType,
    },
    labelLayout: LabelLayout.SideBySide,
  },
  conditionAttributes: null as ConditionAttribute[] | null,
  showButtonDirectly: false,
  subTabId: null as string | null,
};

export const newSection = {
  sizeX: 1,
  sizeY: 1,
  order: 0,
  name: '1',
  title: translateValues,
  description: '',
  collapsed: false,
  visible: true,
  tabId: null as string | null,
  subTabs: null,
  defaultConditionAttributes: null,
  collapseConditionAttributes: null,
  items: [initialItem],
};

export const generateTabId = (): string => crypto.randomUUID();

export const addNewSection = (state: TemplateFormsState, { payload }: { payload: Payload }) => {
  const { value, layoutType, newTab, newSubTab, parentSectionName, currentTab, tabId } = payload;
  const sections = state[layoutType].sections;

  if (newTab && !tabId) return;

  if (newSubTab) {
    if (!parentSectionName) return;

    const parentSectionIndex = findTemplateSectionIndex(sections, parentSectionName, currentTab ?? null);
    if (parentSectionIndex === -1) return;

    const parentSection = sections[parentSectionIndex];
    const subTabs = parentSection.subTabs ?? [];

    if (subTabs.some((st) => st.name === value.name)) {
      throw new Error('exists');
    }

    const maxOrder = subTabs.length > 0 ? Math.max(...subTabs.map((st) => st.order)) : -1;

    const newSubTabEntity = {
      id: crypto.randomUUID(),
      order: maxOrder + 1,
      name: value.name,
      translations: value.title,
    };

    sections[parentSectionIndex].subTabs = [...subTabs, newSubTabEntity];

    const realItems = parentSection.items.filter((item) => item.itemGuid);
    const allSubTabIds = (sections[parentSectionIndex].subTabs ?? []).map((st) => st.id);
    sections[parentSectionIndex].items = generateGridPlaceholders(realItems, parentSection.sizeX, parentSection.sizeY, initialItem, allSubTabIds);
  } else {
    const sectionTabId = newTab ? (tabId ?? null) : (currentTab ?? null);

    if (!isSectionNameUnique(sections, value.name ?? '', sectionTabId)) {
      throw new Error('exists');
    }

    const maxOrder = sections.length > 0 ? Math.max(...sections.map((s) => s.order)) : -1;

    const _newSection = {
      ...newSection,
      sizeX: value.sizeX,
      sizeY: value.sizeY,
      name: value.name,
      title: value.title,
      description: value.description,
      collapsed: value.collapsed,
      tabId: sectionTabId,
      order: maxOrder + 1,
      items: [{ ...initialItem }],
    };

    sections.push(_newSection);
  }
};
